

import { useParentPosition } from "@/hooks/useParentPosition";
import { useState, useEffect, useRef } from "react";
import { PInfo } from "../PInfo/PInfo";

type MultilineInputProps = {
  id: string;
  border?: string;
  rounded?: string;
  margin?: string;
  height?: string;
  label?: string;
  fontSize?: string;
  value?: string;
  onChangeAction: (value: string) => void;
  placeholder?: string;
  error?: string;
  className?: string;
  width?: string;
  ref?: React.Ref<HTMLTextAreaElement | null>;
  disabled?: boolean;
  autoComplete?: boolean;
  surface?: string;
  paddingInput?: string;
  onBlurAction?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onEnterAction?: () => void;
  onFocusAction?: () => void;
  hoverMessage?: string;
  maxLength?: number;
  labelFontSize?: string;
  rows?: number;
  autoGrow?: boolean;
};

export const MultilineInput: React.FC<MultilineInputProps> = ({
  id,
  surface = "",
  border = "outline-secondary border focus-within:outline-focus",
  rounded = "rounded-[10px]",
  margin = "mt-2",
  height = "min-h-25",
  label,
  fontSize = "body-2-regular",
  value = "",
  onChangeAction,
  placeholder = "",
  error,
  className = "",
  width = "",
  ref,
  disabled = false,
  paddingInput = "p-2",
  onBlurAction,
  onEnterAction,
  onFocusAction,
  hoverMessage,
  maxLength,
  labelFontSize = "body-2-medium",
  rows = 4,
  autoGrow = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const labelRef = useRef<HTMLLabelElement>(null);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!disabled) {
      onChangeAction(e.target.value);

      if (autoGrow) {
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight}px`;
      }
    }
  };

  useEffect(() => {
    if (autoGrow && ref && "current" in ref && ref.current) {
      const textarea = ref.current;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [autoGrow, ref, value]);

  return (
    <div className={`flex flex-col ${className} ${width} relative`}>
      {label && (
        <label
          ref={labelRef}
          onMouseEnter={() => {
            setShowTooltip(true);
            setDropdownPosition(labelRef);
          }}
          onMouseLeave={() => setShowTooltip(false)}
          htmlFor={id}
          className={`font-secondary w-max ${labelFontSize} ${
            disabled ? "opacity-50" : ""
          } ${hoverMessage ? "cursor-help" : ""} `}
        >
          {label}
        </label>
      )}
      <textarea
        maxLength={maxLength}
        ref={ref as React.Ref<HTMLTextAreaElement>}
        id={id}
        name={id}
        value={value}
        onChange={handleChange}
        onFocus={() => {
          if (onFocusAction) {
            onFocusAction();
          }
        }}
        onBlur={(e) => onBlurAction && onBlurAction(e)}
        onKeyDown={(e) => {
          // ⌘+Enter on macOS, Ctrl+Enter elsewhere (audit 2026-09-28, re-audit B).
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (onEnterAction) onEnterAction();
          }
        }}
        minLength={rows}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={` ${border} ${margin} ${
          height && !autoGrow ? height : ""
        } ${surface} ${paddingInput} w-full ${rounded} focus:outline-hidden placeholder:font-tertiary placeholder:${fontSize} ${fontSize} ${
          disabled ? "opacity-50" : ""
        }  outline-quaternary`}
      />

      {error && <p className="mt-1 body-3-regular font-error">{error}</p>}
      {showTooltip && hoverMessage && (
        <PInfo message={hoverMessage} position={dropdownPosition} />
      )}
    </div>
  );
};
